import { Copy, Radio, RadioTower, Scissors, ShieldOff, Slash, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PageHeader } from '../components/AppShell';
import { PreviewPlayer } from '../components/PreviewPlayer';
import { Talkback } from '../components/Talkback';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Empty } from '../components/ui/empty';
import { Spinner } from '../components/ui/spinner';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../components/ui/toast';
import {
  type LiveStreamItem,
  type LiveStreamStatus,
  useCutLive,
  useLiveStreams,
  usePromoteLive,
  useRejectLive,
} from '../lib/api/live';
import { fmtRelative } from '../lib/date';

/**
 * Phase 1 live-contributions surface. The list refreshes every 5 s via
 * react-query polling — fine for the MVP given a handful of
 * correspondents at most concurrently. Phase 2 swaps to an SSE
 * subscription so promote/reject from another operator's tab updates
 * everyone immediately.
 *
 * Slot routing (promote-to-slot-N) is wired through the API but the
 * SRS-side alias is still a stub (see liveStreamService.ts). For now
 * the operator pastes the per-stream pull URL into vMix manually; the
 * Phase 2 alias work makes slots 1/2/3 routable URLs.
 */
// Persisted "include OBS / persistent-key sessions in the green room"
// preference. Off by default: persistent feeds are pre-approved by
// virtue of having a named key configured on the Broadcasts page —
// they shouldn't normally need triage, and the operator can still
// reach them (preview + cut + promote) from the Broadcasts detail
// panel. Toggle on to bring them into the green room queue when you
// need to promote one into a vMix slot.
const ALL_SOURCES_KEY = 'live-contributions:show-all-sources';

function readShowAllSources(): boolean {
  try {
    return localStorage.getItem(ALL_SOURCES_KEY) === '1';
  } catch {
    return false;
  }
}

function writeShowAllSources(value: boolean): void {
  try {
    localStorage.setItem(ALL_SOURCES_KEY, value ? '1' : '0');
  } catch {
    /* private mode etc. — ignore */
  }
}

export default function LiveContributionsPage(): JSX.Element {
  const live = useLiveStreams();
  const [rejecting, setRejecting] = useState<LiveStreamItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showAllSources, setShowAllSources] = useState<boolean>(() => readShowAllSources());
  const promote = usePromoteLive();
  const reject = useRejectLive();
  const cut = useCutLive();
  const toast = useToast();

  useEffect(() => {
    writeShowAllSources(showAllSources);
  }, [showAllSources]);

  const streams = live.data?.streams ?? [];
  const slots = live.data?.slots ?? [];
  const ingestHost = live.data?.ingestHost ?? null;

  // Field correspondents have `sourceStreamKeyId == null` (mobile
  // VONPlayer or ad-hoc OBS); persistent-key sessions carry the
  // owning key's id. When the operator hasn't opted into "all
  // sources", drop the persistent ones from the queue.
  const activeStreams = streams.filter((s) => s.status === 'pushing' || s.status === 'pending');
  const hiddenPersistentCount = activeStreams.filter((s) => s.sourceStreamKeyId != null).length;
  const greenRoom = showAllSources
    ? activeStreams
    : activeStreams.filter((s) => s.sourceStreamKeyId == null);

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.showToast({ title: `${label} copied to clipboard`, variant: 'success' });
    } catch {
      toast.showToast({ title: 'Clipboard not available', variant: 'danger' });
    }
  };

  return (
    <>
      <Topbar title="Live contributions" />
      <PageHeader
        title="Live contributions"
        subtitle={
          ingestHost
            ? `Field correspondents → ${ingestHost}. Studio pulls SRT/RTMP per session.`
            : 'Live ingest is not configured on the API. Set LIVE_INGEST_HOST to enable.'
        }
      />
      <div className="space-y-6 px-6 pb-10">
        {!live.data && live.isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner /> Loading…
          </div>
        )}

        {/* On-air slots */}
        <section>
          <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
            On air
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {[1, 2, 3].map((slot) => {
              const slotInfo = slots.find((s) => s.slot === slot);
              const stream = streams.find((s) => s.id === slotInfo?.streamId) ?? null;
              return (
                <Card key={slot}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Slot {slot}</CardTitle>
                    {stream ? (
                      <CardDescription className="flex items-center gap-1.5">
                        <Radio className="h-3.5 w-3.5 text-rose-600" />
                        {stream.correspondent?.name ?? stream.correspondent?.email ?? 'unknown'}
                      </CardDescription>
                    ) : (
                      <CardDescription>No contribution</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-2 pt-0 text-xs">
                    {/* Stable per-slot pull URLs: pre-configure once in
                     *  vMix; the relay republishes whichever
                     *  correspondent is in this slot. Shown even when
                     *  empty so the operator can grab the URLs at
                     *  setup time before any contribution is live. */}
                    <div className="space-y-1">
                      {slotInfo?.pullUrlSrt && (
                        <PullUrlRow
                          label="SRT slot URL"
                          value={slotInfo.pullUrlSrt}
                          onCopy={copy}
                        />
                      )}
                      {slotInfo?.pullUrlRtmp && (
                        <PullUrlRow
                          label="RTMP slot URL"
                          value={slotInfo.pullUrlRtmp}
                          onCopy={copy}
                        />
                      )}
                    </div>
                    {stream && (
                      <>
                        <PreviewPlayer
                          webrtcUrl={stream.webrtcPlayUrl}
                          hlsUrl={stream.hlsPreviewUrl}
                          resetKey={stream.id}
                        />
                        {/* Talkback stays available on air: cueing a
                         *  correspondent mid-hit ("wrap in 30") is the
                         *  main thing an IFB is for. It's a separate SRS
                         *  app, so it can't leak into the outbound
                         *  feed. */}
                        <Talkback
                          publishUrl={stream.talkbackPublishUrl}
                          correspondentName={stream.correspondent?.name}
                        />
                        <div className="text-muted-foreground">
                          {stream.title ?? 'Untitled'} · {stream.locationLabel ?? 'no location'}
                        </div>
                        <div className="text-muted-foreground">
                          on air for {fmtRelative(stream.promotedAt ?? stream.createdAt)}
                        </div>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => cut.mutate({ id: stream.id })}
                          disabled={cut.isPending}
                        >
                          <Slash className="mr-1 h-3.5 w-3.5" /> Cut
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Green room */}
        <section>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Green room
            </h2>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
              <RadioTower className="size-3.5" />
              <span>Include OBS / persistent-key sessions</span>
              {!showAllSources && hiddenPersistentCount > 0 ? (
                <Badge variant="neutral">{hiddenPersistentCount} hidden</Badge>
              ) : null}
              <Switch
                checked={showAllSources}
                onCheckedChange={setShowAllSources}
                aria-label="Include OBS / persistent-key sessions"
              />
            </label>
          </div>
          {greenRoom.length === 0 ? (
            <Empty
              icon={Radio}
              title="No contributions"
              description={
                !showAllSources && hiddenPersistentCount > 0
                  ? `${hiddenPersistentCount} active OBS / persistent-key session(s) are hidden — toggle "Include OBS / persistent-key sessions" above to see them, or open the Broadcasts page.`
                  : 'When a correspondent taps Go Live in VONPlayer, their session will appear here. Promote one into a slot to put it on air.'
              }
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {greenRoom.map((s) => (
                <Card key={s.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <CardTitle className="text-base">
                          {s.correspondent?.name ?? s.correspondent?.email ?? 'unknown'}
                        </CardTitle>
                      </div>
                      <StatusBadge status={s.status} />
                    </div>
                    <CardDescription>
                      {s.title ?? 'Untitled'} · {s.locationLabel ?? 'no location'} · started{' '}
                      {fmtRelative(s.pushStartedAt ?? s.createdAt)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-0">
                    <PreviewPlayer
                      webrtcUrl={s.webrtcPlayUrl}
                      hlsUrl={s.hlsPreviewUrl}
                      resetKey={s.id}
                    />
                    <Talkback
                      publishUrl={s.talkbackPublishUrl}
                      correspondentName={s.correspondent?.name}
                    />
                    {s.bitrateKbps != null || s.packetLossPct != null ? (
                      <div className="text-xs text-muted-foreground">
                        {s.bitrateKbps != null && <span>{s.bitrateKbps} kbps</span>}
                        {s.bitrateKbps != null && s.packetLossPct != null && <span> · </span>}
                        {s.packetLossPct != null && <span>{s.packetLossPct}% loss</span>}
                      </div>
                    ) : null}
                    <div className="space-y-1.5 text-xs">
                      {s.pullUrlSrt && (
                        <PullUrlRow label="SRT" value={s.pullUrlSrt} onCopy={copy} />
                      )}
                      {s.pullUrlRtmp && (
                        <PullUrlRow label="RTMP" value={s.pullUrlRtmp} onCopy={copy} />
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3].map((slot) => (
                        <Button
                          key={slot}
                          size="sm"
                          variant="primary"
                          disabled={s.status !== 'pushing' || promote.isPending}
                          onClick={() => promote.mutate({ id: s.id, slot: slot as 1 | 2 | 3 })}
                        >
                          Promote → Slot {slot}
                        </Button>
                      ))}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setRejecting(s);
                          setRejectReason('');
                        }}
                      >
                        <ShieldOff className="mr-1 h-3.5 w-3.5" /> Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject contribution</DialogTitle>
            <DialogDescription>
              Logged to the audit trail. The correspondent will be kicked from the ingest server and
              asked to retry from the app.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason (visible to the operator audit log only)"
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={!rejectReason.trim() || reject.isPending}
              onClick={() => {
                if (!rejecting) return;
                reject.mutate(
                  { id: rejecting.id, reason: rejectReason.trim() },
                  { onSuccess: () => setRejecting(null) },
                );
              }}
            >
              <Scissors className="mr-1 h-3.5 w-3.5" /> Reject + kick
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PullUrlRow({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (text: string, label: string) => void;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <Badge variant="outline" className="font-mono text-[10px]">
        {label}
      </Badge>
      <code className="flex-1 truncate font-mono text-[11px] text-muted-foreground">{value}</code>
      <Button size="icon" variant="ghost" onClick={() => onCopy(value, `${label} pull URL`)}>
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function StatusBadge({ status }: { status: LiveStreamStatus }): JSX.Element {
  switch (status) {
    case 'on_air':
      return <Badge className="bg-rose-600 text-white">On air</Badge>;
    case 'pushing':
      return <Badge variant="outline">Pushing</Badge>;
    case 'pending':
      return <Badge variant="outline">Connecting…</Badge>;
    case 'ended':
      return <Badge variant="neutral">Ended</Badge>;
    case 'rejected':
      return <Badge variant="danger">Rejected</Badge>;
  }
}
